import { EventEmitter, Injectable } from '@angular/core';
import { BodyOutputType, Toast, ToasterService, ToastType } from 'angular2-toaster';

export const MessageTypes = {
    Wait: 'wait',
    Info: 'info',
    Success: 'success',
    Warning: 'warning',
    Danger: 'danger'
};

export const MessageTimeouts = {
    Short: 3000,
    Standard: 5000,
    Long: 7000
};

export const HandlerType = {
    None: 0,
    Toast: 1,
    Custom: 2
};

export interface MessageCommand {
    commandName: string;
    commandArgs?: any;
};

export interface MessageItem {
    Type: string;
    Message: string;
    RawResponse?: string;
    Count?: number; //TODO: May not need anymore
    Timeout?: number; // Toast display's timeout
    Title?: string;
    MessageData?: any; // Any optional data related to the message - not used now
}

export interface HandlerOptions {
    DangerHandlerType?: number;
    WarningHandlerType?: number;
    SuccessHandlerType?: number;
    InfoHandlerType?: number;
    WaitHandlerType?: number;
}

interface QueuedMessageItem extends MessageItem {
    ExpiredBy?: Date; // Message won't be sent out after ExpiredBy, null/undefined mean message won't be expired
}

interface MessageChannels {
    [key: string]: QueuedMessageItem[];
}

@Injectable()
export class MessageHandlerService {
    public onCommand = new EventEmitter<MessageCommand>();
    public messageComponentArgs: any;

    private _clearAllEmitter = new EventEmitter();
    private _newMessagesEmitter = new EventEmitter<MessageItem[]>();
    private _subscriberStack: any[] = [];
    private _newMessagesSubscriber: any = null; // current newMessage subscriber
    private _clearAllSubscriber: any = null; // current clearAll subscriber

    private _channels: MessageChannels = {};

    private _defaultOptions: HandlerOptions = {
        DangerHandlerType: HandlerType.Toast,
        WarningHandlerType: HandlerType.Toast,
        SuccessHandlerType: HandlerType.Toast,
        InfoHandlerType: HandlerType.Toast,
        WaitHandlerType: HandlerType.Toast
    };

    private _handlerKeyMap: any = {};

    constructor(private _toasterService: ToasterService) {
        this._handlerKeyMap[MessageTypes.Wait] = 'WaitHandlerType';
        this._handlerKeyMap[MessageTypes.Info] = 'InfoHandlerType';
        this._handlerKeyMap[MessageTypes.Success] = 'SuccessHandlerType';
        this._handlerKeyMap[MessageTypes.Warning] = 'WarningHandlerType';
        this._handlerKeyMap[MessageTypes.Danger] = 'DangerHandlerType';
    }

    confirmDiscardChanges(message: string) {
        return window.confirm(message);
    };

    clearAll() {
        this._clearAllEmitter.emit();

        try {
            this._toasterService.clear();
        } catch (ex) {
            // TODO: Figure out why and correct...
            // This fails in some spec tests like option-config.component.spec.ts.
            // When clearAll is called from ngOnDestroy in OptionConfigComponent during the ResetTestingModule cycle,
            // then only the first test works and all others fail.
        }
    }

    //This is for clearing Toast Message only
    clear(msgId: string) {
        this._toasterService.clear(msgId);
    }

    //This always uses Toast Message
    // setMessageComponent(msgType: string, msgComponent: any, timeout?: number, title?: string, compData?: any): string {
    //     this.messageComponentArgs = compData;
    //     let toast: Toast = {
    //         type: msgType.replace(MessageTypes.Danger, 'error'),
    //         body: msgComponent,
    //         bodyOutputType: BodyOutputType.Component,
    //         timeout: timeout,
    //         title: title
    //     };

    //     let toastRef = this._toasterService.pop(toast);
    //     return toastRef.toastId ?? '';
    // }

    setMessage(msgType: string, msg: string, timeout?: number, title?: string, handlerOptions?: HandlerOptions): string {
        let _messages: MessageItem[] = [{ Type: msgType, Message: msg, Timeout: timeout, Title: title }];
        let msgIds: string[] = this.setMessages(_messages, handlerOptions);
        return msgIds && msgIds.length > 0 ? msgIds[0] : '';
    }

    setMessages(messages: MessageItem[] | MessageItem, handlerOptions?: HandlerOptions): string[] {
        let msgIds: string[] = [];
        let custMsgs: MessageItem[] = [];

        let msgs: MessageItem[] = <MessageItem[]>(messages && !Array.isArray(messages) ? [messages] : messages);
        if (!msgs || msgs.length <= 0) { return msgIds; }

        let handlers = this._getHandlerOptionsWithDefaults(handlerOptions);
        if (handlers || true) {
            for (let _msg of msgs) {
                if (_msg.Type === MessageTypes.Danger) {
                    console.error('MessageHandlerService:->', _msg);
                }

                let handlerType: number = (<any>handlers)[this._handlerKeyMap[`${_msg.Type}`]];
                if (handlerType === HandlerType.Toast) {
                    let toast: Toast = {
                        type: _msg.Type.replace(MessageTypes.Danger, 'error') as ToastType,
                        body: _msg.Message,
                        bodyOutputType: BodyOutputType.TrustedHtml,
                        timeout: _msg.Timeout,
                        title: _msg.Title
                    };

                    let toastRef = this._toasterService.pop(toast);
                    if(toastRef.toastId) {
                        msgIds.push(toastRef.toastId);
                    }
                } else if (handlerType === HandlerType.Custom) {
                    custMsgs.push(_msg);
                }
            }

            if (custMsgs.length > 0) {
                this._newMessagesEmitter.emit(custMsgs);
            }
        }

        return msgIds;
    }

    //Insert a message into a channel to be retrieved later
    //If channelName is empty, "_default" channel will be used
    //If expiredAfter is <=0, the message won't be expired; if >0, the message will be ignored after that number of ms.
    queueMessage(channelName: string, expiredAfter: number, msgType: string, msg: string, timeout?: number, title?: string, data?: any) {
        channelName = channelName || '_default';
        let msgChannel = <QueuedMessageItem[]>(this._channels[channelName]);
        if (!msgChannel) {
            msgChannel = this._channels[channelName] = [];
        } else {
            //Remove all expired messages from channel
            this._purgeChannel(channelName);
        }

        let expiredBy!: Date;
        if (expiredAfter && expiredAfter > 0) {
            expiredBy = new Date((new Date()).getTime() + expiredAfter);
        }

        msgChannel.push({ Type: msgType, Message: msg, Timeout: timeout, Title: title, ExpiredBy: expiredBy, MessageData: data });
    }

    //Release all non-expired messages from a channel
    releaseMessages(channelName?: string, handlerOptions?: HandlerOptions) {
        this._purgeChannel(channelName || '_default', handlerOptions, true);
    }

    //provide clearAllHandler to also get notified when clearAll method is called
    subscribe(messageHandler?: (messages: MessageItem[]) => void,
        error?: any,
        complete?: any,
        clearAllHandler?: () => void) {
        if (!messageHandler && !error && !complete) { return undefined; }

        let stackItem = { next: messageHandler, error: error, complete: complete, clearAllHandler: clearAllHandler };
        this._subscriberStack.push(stackItem);

        this._updateCurrentSubscriber();

        return {
            unsubscribe: () => {
                this._unsubscribe(stackItem);
            }
        };
    }

    setDefaultOptions(options: HandlerOptions, replaceAll = false) {
        if (replaceAll) {
            this._defaultOptions = options;
        } else {
            Object.assign(this._defaultOptions, options);
        }
    }

    private _updateCurrentSubscriber() {
        if (this._newMessagesSubscriber) {
            this._newMessagesSubscriber.unsubscribe();
            this._newMessagesSubscriber = null;
        }

        if (this._clearAllSubscriber) {
            this._clearAllSubscriber.unsubscribe();
            this._clearAllSubscriber = null;
        }

        if (this._subscriberStack.length > 0) {
            let nextItem: any = this._subscriberStack[this._subscriberStack.length - 1];
            this._newMessagesSubscriber = this._newMessagesEmitter.subscribe(nextItem.next, nextItem.error, nextItem.complete);
            if (nextItem.clearAllHandler) {
                this._clearAllSubscriber = this._clearAllEmitter.subscribe(nextItem.clearAllHandler);
            }
        }
    }

    private _unsubscribe = (item: any) => {
        if (!this._subscriberStack || this._subscriberStack.length <= 0) { return; }

        let idx = this._subscriberStack.indexOf(item);
        if (idx >= 0) { this._subscriberStack.splice(idx, 1); }

        if (idx === this._subscriberStack.length) {
            //was last item, currently the active observer
            this._updateCurrentSubscriber();
        }
    }

    private _getHandlerOptionsWithDefaults(options?: HandlerOptions): HandlerOptions {
        let allOptions = <HandlerOptions>{};
        Object.assign(allOptions, this._defaultOptions, options);
        return allOptions;
    }

    /*
      Set releaseMessages=true will push all messages in the requested channel out to current subscriber/toaster container.
    */
    private _purgeChannel(channelName: string, handlerOptions?: HandlerOptions, releaseMessages = false) {
        if (!channelName) { return; }

        let msgChannel = <QueuedMessageItem[]>(this._channels[channelName]);
        if (msgChannel && msgChannel.length > 0) {
            let dtNow = new Date();

            let _messages = (msgChannel.splice(0)).filter(m => !m.ExpiredBy || m.ExpiredBy >= dtNow);
            if (_messages.length > 0) {
                if (releaseMessages) {
                    this.setMessages(_messages, handlerOptions);
                } else {
                    msgChannel.push.apply(msgChannel, _messages);
                }
            }
        }
    }
}
